import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "../lib/api";

const STORAGE_KEY = "ssc_token";
const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);   // null = unknown, false = logged-out, object = logged-in
  const [token, setTokenState] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || ""; } catch { return ""; }
  });

  const setToken = useCallback((t) => {
    setTokenState(t || "");
    try {
      if (t) localStorage.setItem(STORAGE_KEY, t);
      else localStorage.removeItem(STORAGE_KEY);
    } catch { /* ignore */ }
    if (t) api.defaults.headers.common["Authorization"] = `Bearer ${t}`;
    else delete api.defaults.headers.common["Authorization"];
  }, []);

  // Apply token to axios immediately on mount
  useEffect(() => {
    if (token) api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  }, [token]);

  // Hydrate user from token
  useEffect(() => {
    let alive = true;
    if (!token) { setUser(false); return; }
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (alive) setUser(data.user);
      } catch {
        if (alive) { setToken(""); setUser(false); }
      }
    })();
    return () => { alive = false; };
  }, [token, setToken]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setToken(data.token);
    setUser(data.user);
    return data.user;
  }, [setToken]);

  const signup = useCallback(async (payload) => {
    const { data } = await api.post("/auth/signup", payload);
    setToken(data.token);
    setUser(data.user);
    return data.user;
  }, [setToken]);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    setToken("");
    setUser(false);
  }, [setToken]);

  const updateProfile = useCallback(async (patch) => {
    const { data } = await api.put("/auth/me", patch);
    setUser(data.user);
    return data.user;
  }, []);

  const value = { user, token, login, signup, logout, updateProfile };
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

/** Convert a FastAPI error payload into a string. */
export function formatApiError(e) {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || "Something went wrong.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(x => x?.msg || JSON.stringify(x)).join(" ");
  if (d?.msg) return d.msg;
  return String(d);
}
